<script lang="ts" setup>
import { CustomerAddress } from '@/core/collections/contracts-api/customers/customer-addresses/customer-address.interface';
import {
   formatCustomerAddressPostalCity,
   formatCustomerAddressStreetHouseBox,
   getCustomerAddressTypeDisplayName,
} from '@/core/helpers/customer-address-format.helper';
import { PropType, computed } from 'vue';
import CountryFlag from 'vue-country-flag-next';
import { useI18n } from 'vue-i18n';

const props = defineProps({
   address: { type: Object as PropType<CustomerAddress>, required: true },
   showAddressType: { type: Boolean, default: true },
});

const { locale } = useI18n();

const typeLabel = computed(() =>
   props.showAddressType ? getCustomerAddressTypeDisplayName(props.address.customer_address_type, locale.value) : '',
);

const streetLine = computed(() => formatCustomerAddressStreetHouseBox(props.address));
const cityLine = computed(() => formatCustomerAddressPostalCity(props.address, locale.value, { includeCountry: true }));
const countryIso = computed(() => props.address.city?.country?.iso_code_2?.trim() || '');
</script>

<template>
   <div class="customer-address-dropdown-label">
      <p class="customer-address-dropdown-label__inner m-0">
         <span v-if="typeLabel" class="customer-address-dropdown-label__type">
            <strong>{{ typeLabel }}</strong>
            <span class="customer-address-dropdown-label__type-sep">:</span>
         </span>
         <span class="customer-address-dropdown-label__addr">
            <span>{{ streetLine }}</span>
            <template v-if="cityLine">
               <span class="customer-address-dropdown-label__comma">,</span>
               <span class="customer-address-dropdown-label__after-comma">{{ cityLine }}</span>
            </template>
            <span v-if="countryIso" class="customer-address-dropdown-label__flag-wrap">
               <CountryFlag size="big" :country="countryIso" />
            </span>
         </span>
      </p>
   </div>
</template>

<style lang="scss" scoped>
.customer-address-dropdown-label__inner {
   display: flex;
   flex-wrap: wrap;
   align-items: center;
}

.customer-address-dropdown-label__type + .customer-address-dropdown-label__addr {
   padding-left: 0.35em;
}

.customer-address-dropdown-label__addr {
   display: inline-flex;
   flex-wrap: wrap;
   align-items: center;
}

.customer-address-dropdown-label__after-comma {
   padding-left: 0.25em;
}

/* vue-country-flag-next base sprite is 52×39px; scale does not affect layout — clip in a fixed slot. */
.customer-address-dropdown-label__flag-wrap {
   display: inline-block;
   position: relative;
   flex-shrink: 0;
   width: toRem(16);
   height: toRem(12);
   margin-left: toRem(4);
   overflow: hidden;
   vertical-align: -0.125em;
}

.customer-address-dropdown-label__flag-wrap :deep(.flag) {
   position: absolute;
   left: 50%;
   top: 50%;
   margin: 0 !important;
   transform: translate(-50%, -50%) scale(0.308);
   transform-origin: center center;
}
</style>
